import { useState, useRef } from "react";
import Layout from "@/components/feature/Layout";
import SEOHead from "@/components/feature/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function About() {
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formMsg, setFormMsg] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 韩漫漫画网",
    description:
      "了解韩漫漫画网的品牌故事、发展历程和联系方式。韩漫漫画网是专业的韩漫画免费网站，提供漫画大全和韩漫完整在线观看服务。",
    url: "https://www.hanmancomic.com/about",
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "韩漫漫画网是正规平台吗？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "韩漫漫画网是专业的韩漫在线阅读平台，致力于为广大漫画爱好者提供优质的韩漫阅读服务。我们严格遵守相关法律法规，保证平台的合法性。",
        },
      },
      {
        "@type": "Question",
        name: "如何联系韩漫漫画网客服？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "您可以通过以下方式联系我们：发送邮件至contact@hanmancomic.com，或添加客服QQ：1234567890。我们的服务时间为每天09:00至21:00。",
        },
      },
      {
        "@type": "Question",
        name: "韩漫漫画网有手机APP吗？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "目前韩漫漫画网主要通过网页端提供服务，支持手机、平板和电脑等多种设备在线访问，网站采用响应式设计，在移动端拥有流畅的阅读体验。",
        },
      },
    ],
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus("success");
      setFormMsg("感谢您的留言，我们会尽快回复！");
      form.reset();
      return;
    }
    setFormStatus("loading");
    setFormMsg("");
    try {
      const formData = new FormData(form);
      const response = await fetch("https://readdy.ai/api/form/d9pdn4gsenbniolestg0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setFormMsg("感谢您的留言，我们会尽快回复！");
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          "提交失败，请稍后再试。";
        setFormStatus("error");
        setFormMsg(serverMsg);
      }
    } catch {
      setFormStatus("error");
      setFormMsg("网络错误，请检查网络后重试。");
    }
  };

  const statsData = [
    { value: "1,256+", label: "收录漫画" },
    { value: "500万+", label: "月活跃用户" },
    { value: "98.5%", label: "用户好评率" },
    { value: "7年", label: "运营时间" },
  ];

  return (
    <Layout>
      <SEOHead
        title="关于我们 - 韩漫漫画网"
        description="了解韩漫漫画网的品牌故事、发展历程和联系方式。韩漫漫画网是专业的韩漫画免费网站，提供漫画大全和韩漫完整在线观看服务。"
        keywords="韩漫漫画网,关于我们,韩漫画免费网站,联系我们,品牌介绍"
        canonicalUrl="https://www.hanmancomic.com/about"
        schemaJson={[aboutSchema, faqSchema]}
      />

      <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: "关于我们" },
          ]}
        />

        <section className="mt-4">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
            关于韩漫漫画网
          </h1>
          <p className="text-sm text-foreground-600 max-w-3xl leading-relaxed mb-8">
            韩漫漫画网作为<strong>韩漫画免费网站</strong>，致力于为全球华语读者提供最优质的<strong>漫画大全</strong>和<strong>韩漫完整在线观看</strong>服务。
            自成立以来，我们始终坚持以用户为中心，不断丰富内容库，优化阅读体验。
          </p>
        </section>

        <section className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
          {statsData.map((stat) => (
            <div
              key={stat.label}
              className="p-4 md:p-5 rounded-lg border border-background-200/70 bg-background-50 text-center"
            >
              <div className="text-xl md:text-2xl font-heading font-bold text-primary-500 mb-1">
                {stat.value}
              </div>
              <div className="text-xs text-foreground-500">{stat.label}</div>
            </div>
          ))}
        </section>

        <section className="mb-12">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
            品牌故事
          </h2>
          <div className="bg-background-100/70 rounded-lg p-5 md:p-6 border border-background-200/70">
            <p className="text-sm text-foreground-600 leading-relaxed mb-3">
              韩漫漫画网诞生于一个简单的想法：让更多人能够轻松地接触到优质的韩国漫画。
              最初由一群热爱韩漫的年轻人创立，经过多年的发展，韩漫漫画网已经成为华语地区最受欢迎的韩漫阅读平台之一。
            </p>
            <p className="text-sm text-foreground-600 leading-relaxed mb-3">
              我们与多家韩国漫画出版社建立了合作关系，确保内容的丰富性和更新的及时性。
              无论是热门的连载作品还是经典的完结漫画，韩漫漫画网都能为读者提供完整的阅读体验。
            </p>
            <p className="text-sm text-foreground-600 leading-relaxed">
              未来，韩漫漫画网将继续深耕韩漫领域，不断拓展内容库，优化阅读功能，
              为全球华语韩漫爱好者带来更优质的阅读体验。
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
            读者评价
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                name: "漫画爱好者小王",
                rating: 5,
                text: "韩漫漫画网真的太好用了！更新快，画质清晰，分类也很全。我每天都会来这里看最新的韩漫更新，已经推荐给了很多朋友。",
              },
              {
                name: "资深漫迷阿琳",
                rating: 5,
                text: "作为看了五年韩漫的老读者，韩漫漫画网是我用过最棒的韩漫平台。界面简洁，加载速度快，而且完全免费，强烈推荐！",
              },
              {
                name: "学生读者小陈",
                rating: 4,
                text: "韩漫漫画网的漫画资源很丰富，各种类型都有。手机阅读体验也很好，上课路上就能追更，非常方便。希望能增加更多完结作品。",
              },
            ].map((review, i) => (
              <div
                key={i}
                className="p-4 rounded-lg border border-background-200/70 bg-background-50"
              >
                <div className="flex items-center gap-1 mb-2">
                  {Array.from({ length: 5 }, (_, j) => (
                    <i
                      key={j}
                      className={`ri-star-fill text-xs ${
                        j < review.rating ? "text-accent-500" : "text-foreground-300"
                      }`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  &ldquo;{review.text}&rdquo;
                </p>
                <p className="text-xs text-foreground-500 font-medium">
                  — {review.name}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
            常见问题
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {faqSchema.mainEntity.map((faq, i) => (
              <div
                key={i}
                className="p-4 rounded-lg border border-background-200/70 bg-background-50"
              >
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                  {faq.name}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {faq.acceptedAnswer.text}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-2xl">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
            联系我们
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="p-4 rounded-lg border border-background-200/70 bg-background-50 text-center">
              <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center">
                <i className="ri-mail-line text-xl text-primary-500"></i>
              </div>
              <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
              <p className="text-sm text-foreground-800 font-medium">
                contact@hanmancomic.com
              </p>
            </div>
            <div className="p-4 rounded-lg border border-background-200/70 bg-background-50 text-center">
              <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-xl text-primary-500"></i>
              </div>
              <p className="text-xs text-foreground-500 mb-1">客服QQ</p>
              <p className="text-sm text-foreground-800 font-medium">1234567890</p>
            </div>
            <div className="p-4 rounded-lg border border-background-200/70 bg-background-50 text-center">
              <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center">
                <i className="ri-time-line text-xl text-primary-500"></i>
              </div>
              <p className="text-xs text-foreground-500 mb-1">服务时间</p>
              <p className="text-sm text-foreground-800 font-medium">09:00 - 21:00</p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
            <h3 className="text-base font-semibold text-foreground-900 mb-4">
              给我们留言
            </h3>
            <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="">
              <input
                type="text"
                name="website_alt"
                className="form-honeypot"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="contact-subject"
                  type="text"
                  name="subject"
                  className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入留言主题"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                  placeholder="请输入您的留言内容（500字以内）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              {formMsg && (
                <div
                  className={`mb-4 p-3 rounded-lg text-sm ${
                    formStatus === "success"
                      ? "bg-accent-100 text-accent-700 border border-accent-200"
                      : "bg-secondary-100 text-secondary-700 border border-secondary-200"
                  }`}
                >
                  {formMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === "loading"}
                className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
              >
                {formStatus === "loading" ? "发送中..." : "发送留言"}
              </button>
            </form>
          </div>
        </section>
      </div>
    </Layout>
  );
}